import axios from 'axios';
import { encodeStationId, getStationId, isValidStationId } from '../utils/stationId.js';

export const fetchNetworkDevice = async ({ stationId }) => {
    // Normalize and validate station ID
    const normalizedId = getStationId(stationId);
    
    if (!isValidStationId(normalizedId)) {
        console.error('[fetchNetworkDevice] Invalid stationId:', { raw: stationId, normalized: normalizedId });
        throw new Error(`Invalid station ID: ${typeof stationId === 'object' ? '[object Object]' : stationId}`);
    }
    
    console.log('[fetchNetworkDevice] stationId:', normalizedId);
    
    try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/fetch/station?stationId=${encodeURIComponent(normalizedId)}`);
        return response.data;
    } catch (error) {
        console.error('[fetchNetworkDevice] Error:', error);
        throw error;
    }
};

export const fetchNetworkDeviceStatus = async ({ stationId }) => {
    // Normalize and validate station ID
    const normalizedId = getStationId(stationId);
    
    if (!isValidStationId(normalizedId)) {
        console.error('[fetchNetworkDeviceStatus] Invalid stationId:', { raw: stationId, normalized: normalizedId });
        throw new Error(`Invalid station ID: ${typeof stationId === 'object' ? '[object Object]' : stationId}`);
    }
    
    console.log('[fetchNetworkDeviceStatus] stationId:', normalizedId);
    
    try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/fetch/stationStatus?stationId=${encodeURIComponent(normalizedId)}`);
        return response.data;
    } catch (error) {
        console.error('[fetchNetworkDeviceStatus] Error:', error);
        throw error;
    }
}

export const fetchNetworkDeviceData = async ({ stationId }) => {
    // Normalize and validate station ID
    const normalizedId = getStationId(stationId);
    
    if (!isValidStationId(normalizedId)) {
        console.error('[fetchNetworkDeviceData] Invalid stationId:', { raw: stationId, normalized: normalizedId });
        throw new Error(`Invalid station ID: ${typeof stationId === 'object' ? '[object Object]' : stationId}`);
    }
    
    console.log('[fetchNetworkDeviceData] stationId:', normalizedId);
    
    try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/fetch/station-data?stationId=${encodeURIComponent(normalizedId)}`);
        return response.data;
    } catch (error) {
        console.error('[fetchNetworkDeviceData] Error:', error);
        throw error;
    }
};

// Note: This function is the main one used throughout the app.
//
// Second argument is an optional `{ connectorId }` — when
// supplied, the backend's `derived` block targets that
// specific connector's OCPP state (per-connector isolation).
// When omitted, `derived` is the station-wide aggregation
// (backwards compatible with every existing caller).
export const fetchStationDataById = async (stationId, { connectorId } = {}) => {
    // Normalize and validate station ID (handle both string and object inputs)
    const normalizedId = getStationId(stationId);

    if (!isValidStationId(normalizedId)) {
        console.error('[fetchStationDataById] Invalid stationId:', { raw: stationId, normalized: normalizedId });
        throw new Error(`Invalid station ID: ${typeof stationId === 'object' ? '[object Object]' : stationId}`);
    }

    console.log('[fetchStationDataById] stationId:', normalizedId, 'connectorId:', connectorId ?? '(none)');

    const stationIdEncoded = encodeURIComponent(normalizedId);
    const params = { stationId: stationIdEncoded };
    if (connectorId != null && connectorId !== '') {
        params.connectorId = connectorId;
    }
    try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/fetch/station-data`, { params });
        return response.data;
    } catch (error) {
        console.error('[fetchStationDataById] Error:', error);
        throw error;
    }
};

export const fetchPublishedStations = async () => {
    try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/stations`, {
            params: { published: true, active: true }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching published stations:', error);
        throw error;
    }
};

/**
 * Fetch a single station by ID (even if unpublished)
 * This is used for direct access via QR codes or deep links
 *
 * @param {string} stationId - The station ID to fetch
 * @returns {Promise<object>} Station data or null if not found
 */
export const fetchStationById = async (stationId) => {
    try {
        const encodedId = encodeStationId(stationId);
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/stations/${encodedId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching station ${stationId}:`, error);

        // Return null for 404s (station not found) instead of throwing
        if (error.response?.status === 404) {
            return null;
        }

        throw error;
    }
};

/**
 * Fetch a station under an admin preview token.
 *
 * Hits `GET /api/stations/:stationId/preview?token=…` which is the
 * ONLY server surface that returns unpublished / inactive stations
 * to a non-admin caller — and it only does so after validating the
 * signed token against this exact stationId.
 *
 * Returns a discriminated result so the caller can render a
 * dedicated "preview not authorised" state instead of silently
 * falling back to the public endpoint (which would defeat the
 * whole point of scoping the token).
 *
 * @param {string} stationId
 * @param {string} previewToken
 * @returns {Promise<
 *   | { ok: true, station: object }
 *   | { ok: false, error: string, status: number }
 * >}
 */
export const fetchStationPreview = async (stationId, previewToken) => {
    if (!previewToken) {
        return { ok: false, error: 'missing_token', status: 401 };
    }
    try {
        const encodedId = encodeStationId(stationId);
        const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/stations/${encodedId}/preview`,
            { params: { token: previewToken } },
        );
        return { ok: true, station: response.data };
    } catch (error) {
        const status = error?.response?.status ?? 0;
        const body = error?.response?.data;
        // Never log the token — the URL captured by axios.error may
        // contain it. Log the shape of the failure only.
        console.error(
            `[fetchStationPreview] station=${stationId} status=${status} error=${body?.error || 'unknown'}`,
        );
        return {
            ok: false,
            error: body?.error || (status === 0 ? 'network_error' : `http_${status}`),
            status,
        };
    }
};
